import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SubNav } from '@/components/ui/sub-nav';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

function DeleteAccountPage() {
  const { accessToken, logout } = useAuth();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/v1/auth/me`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ confirmDelete: true }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to delete account');
      }
      return response.json();
    },
    onSuccess: () => {
      logout();
      navigate({ to: '/' });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const subNavItems = [
    { to: '/profile', label: 'Basic Info' },
    { to: '/profile/notifications', label: 'Notifications' },
    { to: '/profile/delete', label: 'Delete Account' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Delete Account</h1>
        <p className="text-muted-foreground">Permanently remove your account and data</p>
      </div>

      <SubNav items={subNavItems} />

      <div className="max-w-2xl">
        <Card className="border-destructive/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/20">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-destructive">Danger Zone</CardTitle>
                <CardDescription>
                  This action is permanent and cannot be undone
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-destructive/10 p-4">
              <p className="text-sm text-destructive">
                Deleting your account will permanently remove all your data, projects, and settings.
                This action cannot be reversed.
              </p>
            </div>

            {error && (
              <div className="rounded-lg bg-destructive/15 p-4 text-sm text-destructive">
                {error}
              </div>
            )}

            {!showConfirm ? (
              <Button variant="destructive" onClick={() => setShowConfirm(true)}>
                Delete Account
              </Button>
            ) : (
              <div className="rounded-lg border border-border bg-card p-4">
                <p className="mb-4 font-medium text-foreground">
                  Are you absolutely sure? This cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Yes, Delete My Account'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowConfirm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/profile/delete')({
  component: DeleteAccountPage,
});
